import React, { useMemo } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  Collapse,
  CircularProgress,
  Menu,
} from '@mui/material';
import {
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  PictureInPicture2,
  AlignJustify,
} from 'lucide-mui';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay10Icon from '@mui/icons-material/Replay10';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useSelector } from 'react-redux';

import useLimits from '../plan/hooks/limits';
import { RootState } from '../../reducers';
import {
  formatTime,
  parseTimestampsFromText,
  timeStringToSeconds,
} from './videoUtils';
import type { TimeSegment } from './videoUtils';
import { useTags } from '../../hooks/useTags';
import { VIDEO_CHAPTERS_VISIBLE_KEY } from '../../constants/tags';

interface VideoControlsProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  isPlaying: boolean;
  onPlayPause: () => void;
  isAudioOnly?: boolean;
  progress: number;
  duration: number;
  file?: any;
  onToggleSegments?: () => void;
  showSegments?: boolean;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  videoRef,
  isPlaying,
  onPlayPause,
  isAudioOnly = false,
  progress: externalProgress,
  duration: externalDuration,
  file,
  onToggleSegments,
  showSegments = true,
}) => {
  const oldSummary = useSelector(
    (state: RootState) => state.selectedVideo?.summary?.text || ''
  );
  const summary = useSelector(
    (state: RootState) =>
      state.selectedVideo?.postProcess?.systemPromptResponses?.CHAPTERS
        ?.responseText
  );

  const chapters = useMemo(() => {
    try {
      return JSON.parse(summary).chapters;
    } catch (error) {
      console.error('Error parsing chapters:', error);
      return [];
    }
  }, [summary]) as {
    title: string;
    notes: string;
    start: string;
    end: string;
  }[];

  const timeSegments: TimeSegment[] = useMemo(() => {
    return (
      chapters.map((chapter, index) => ({
        start: timeStringToSeconds(chapter.start),
        description: chapter.title,
        end:
          index === chapters.length - 1
            ? externalDuration
            : timeStringToSeconds(chapters[index + 1].start),
      })) || parseTimestampsFromText(oldSummary)
    );
  }, [chapters, externalDuration, oldSummary]);

  const theme = useTheme();
  const guardLimits = useLimits();
  const [playbackSpeed, setPlaybackSpeed] = React.useState<number>(1);
  const [hoverTime, setHoverTime] = React.useState<string>('');
  const [tooltipPosition, setTooltipPosition] = React.useState<number | null>(
    null
  );
  const [isMuted, setIsMuted] = React.useState(false);
  const [volume, setVolume] = React.useState(100);
  const [showVolumeSlider, setShowVolumeSlider] = React.useState(false);
  const [hoveredSegment, setHoveredSegment] = React.useState<number | null>(
    null
  );
  const [isDragging, setIsDragging] = React.useState(false);
  const [localProgress, setLocalProgress] = React.useState(0);
  const [isMediaLoading, setIsMediaLoading] = React.useState(false);
  const { setTag } = useTags();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleToggleSegments = React.useCallback(() => {
    if (onToggleSegments) {
      onToggleSegments();
      setTag({
        key: VIDEO_CHAPTERS_VISIBLE_KEY,
        value: (!showSegments).toString(),
      });
    }
  }, [onToggleSegments, showSegments, setTag]);

  // Move renderPlayPauseContent inside the component
  const renderPlayPauseContent = () => {
    if (isMediaLoading) {
      return (
        <CircularProgress
          size={32}
          thickness={4}
          sx={{
            color: isAudioOnly ? theme.palette.text.primary : 'white',
          }}
        />
      );
    }
    return isPlaying ? <PauseIcon /> : <PlayArrowIcon />;
  };

  // Calculate which progress value to display
  const displayProgress = isDragging ? localProgress : externalProgress;

  const handleSpeedChange = (event: SelectChangeEvent<number>) => {
    const speed = event.target.value as number;
    setPlaybackSpeed(speed);
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.playbackRate = speed;
    }

    guardLimits('advancedPlayback', () => {
      // This function will be called if the user has permissions
      // We don't need to do anything here since we've already changed the speed
    });
  };

  const handleSkip = (seconds: number) => {
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.currentTime += seconds;
    }
  };

  const handleFullScreen = () => {
    const videoElement = videoRef.current;
    if (videoElement) {
      if (!document.fullscreenElement) {
        videoElement.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    setLocalProgress(value as number);
    setIsDragging(true);
  };

  const handleSliderChangeCommitted = (
    _: Event | React.SyntheticEvent,
    value: number | number[]
  ) => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const newTime = ((value as number) / 100) * videoElement.duration;
      videoElement.currentTime = newTime;
    }
    setIsDragging(false);
  };

  const handleSliderHover = (event: React.MouseEvent<HTMLDivElement>) => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const sliderElement = event.currentTarget;
      const rect = sliderElement.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const hoverTimeInSeconds = (offsetX / rect.width) * externalDuration;
      setHoverTime(formatTime(hoverTimeInSeconds));
      setTooltipPosition(offsetX);
    }
  };

  const handleSliderLeave = () => {
    setTooltipPosition(null);
  };

  const handleMuteToggle = () => {
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (event: Event, value: number | number[]) => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const newVolume = value as number;
      videoElement.volume = newVolume / 100;
      setVolume(newVolume);
      if (newVolume === 0) {
        setIsMuted(true);
      } else {
        setIsMuted(false);
      }
    }
  };

  const iconButtonSx = {
    color: isAudioOnly ? theme.palette.text.primary : 'white',
    '& svg:not(.MuiSvgIcon-root)': {
      fill: 'none',
      stroke: isAudioOnly ? theme.palette.text.primary : 'white',
      strokeWidth: 1.75,
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      transform: 'scale(1)',
    },
    '& .MuiSvgIcon-root': {
      color: isAudioOnly ? theme.palette.text.primary : 'white',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      transform: 'scale(1)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      '& svg:not(.MuiSvgIcon-root), & .MuiSvgIcon-root': {
        transform: 'scale(1.3)',
        color: isAudioOnly ? theme.palette.text.primary : 'white',
      },
      '& svg:not(.MuiSvgIcon-root)': {
        stroke: isAudioOnly ? theme.palette.text.primary : 'white',
      },
    },
  };

  const playPauseButtonSx = {
    ...iconButtonSx,
    mr: 1,
    width: 40,
    height: 40,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      width: 28,
      height: 28,
      color: isAudioOnly ? theme.palette.text.primary : 'white',
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shorter,
      }),
      opacity: isMediaLoading ? 0 : 1,
    },
    '&:hover .MuiSvgIcon-root': {
      transform: 'scale(1.3)',
      color: isAudioOnly ? theme.palette.text.primary : 'white',
    },
    '& .MuiCircularProgress-root': {
      position: 'absolute',
      opacity: 1,
      transition: 'none',
      transform: 'none',
      '&:hover': {
        transform: 'none',
      },
    },
  };

  // First, let's create a helper function to determine the background color
  const segmentColors = useMemo(
    () => ({
      hovered: isAudioOnly ? theme.palette.secondary.main : '#FFFFFF',
      completed: theme.palette.primary.main,
      remaining: theme.palette.secondary.main,
    }),
    [theme, isAudioOnly]
  );

  const getSegmentBackgroundColor = (
    isHovered: boolean,
    isCompleted: boolean
  ) => {
    if (isHovered) return segmentColors.hovered;
    return isCompleted ? segmentColors.completed : segmentColors.remaining;
  };

  // First, add a debug function
  const handleSegmentClick = (e: React.MouseEvent, seconds: number) => {
    e.stopPropagation();
    const videoElement = videoRef.current;
    if (!videoElement) return;

    setIsMediaLoading(true);
    setLocalProgress((seconds / externalDuration) * 100);
    setIsDragging(true);
    videoElement.currentTime = seconds;

    const cleanup = () => {
      setIsMediaLoading(false);
      setIsDragging(false);
      if (!isPlaying) onPlayPause();
    };

    videoElement.addEventListener('seeked', function handler() {
      cleanup();
      videoElement.removeEventListener('seeked', handler);
    });

    // Fallback cleanup
    setTimeout(cleanup, 2000);
  };

  const handleSpeedClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSpeedClose = () => {
    setAnchorEl(null);
  };

  const controlsLayout = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        py: 1,
        width: '100%',
        bgcolor: isAudioOnly ? 'background.paper' : 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Back 10 Seconds" arrow>
            <IconButton
              onClick={() => handleSkip(-10)}
              sx={{ ...iconButtonSx, mr: 1 }}
            >
              <Replay10Icon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isPlaying ? 'Pause' : 'Play'} arrow>
            <IconButton onClick={onPlayPause} sx={playPauseButtonSx}>
              {renderPlayPauseContent()}
            </IconButton>
          </Tooltip>
          <Tooltip title="Skip 10 Seconds" arrow>
            <IconButton
              onClick={() => handleSkip(10)}
              sx={{ ...iconButtonSx, mr: 1 }}
            >
              <Forward10Icon />
            </IconButton>
          </Tooltip>

          <Box
            sx={{ display: 'flex', alignItems: 'center', ml: 1 }}
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <Tooltip title={isMuted ? 'Unmute' : 'Mute'} arrow>
              <IconButton onClick={handleMuteToggle} sx={iconButtonSx}>
                {isMuted ? <VolumeX /> : <Volume2 />}
              </IconButton>
            </Tooltip>
            <Collapse in={showVolumeSlider} orientation="horizontal">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Slider
                  value={volume}
                  onChange={handleVolumeChange}
                  aria-labelledby="volume-slider"
                  sx={{
                    width: 100,
                    ml: 1,
                    color: isAudioOnly ? theme.palette.text.primary : 'white',
                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                    '& .MuiSlider-track': { height: 4 },
                    '& .MuiSlider-rail': { height: 4 },
                  }}
                />
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              color: isAudioOnly ? theme.palette.text.primary : 'white',
              mr: 3,
              lineHeight: 1,
            }}
          >
            {formatTime(videoRef.current?.currentTime || 0)}
            {formatTime(externalDuration) && `/${formatTime(externalDuration)}`}
          </Typography>

          <Tooltip title="Playback Speed" arrow>
            <Typography
              variant="body2"
              onClick={handleSpeedClick}
              sx={{
                fontSize: '0.75rem',
                color: isAudioOnly ? theme.palette.text.primary : 'white',
                cursor: 'pointer',
                transition: theme.transitions.create(['transform'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              {playbackSpeed}x
            </Typography>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleSpeedClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                bgcolor: 'background.paper',
                width: 300,
                p: 2,
              },
            }}
          >
            <Box sx={{ px: 2 }}>
              <Slider
                value={playbackSpeed}
                onChange={(_, value) => {
                  handleSpeedChange({
                    target: { value },
                  } as SelectChangeEvent<number>);
                  setTimeout(() => {
                    handleSpeedClose();
                  }, 500);
                }}
                min={0.5}
                max={3}
                step={0.5}
                marks={[
                  { value: 0.5, label: '0.5x' },
                  { value: 1, label: '1x' },
                  { value: 1.5, label: '1.5x' },
                  { value: 2, label: '2x' },
                  { value: 2.5, label: '2.5x' },
                  { value: 3, label: '3x' },
                ]}
                sx={{
                  '& .MuiSlider-rail': {
                    opacity: 0.25,
                  },
                  '& .MuiSlider-track': {
                    border: 'none',
                  },
                  '& .MuiSlider-thumb': {
                    width: 12,
                    height: 12,
                    '&:before': {
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
                    },
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  },
                  '& .MuiSlider-mark': {
                    display: 'none',
                  },
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.75rem',
                    '&[data-index="1"]': {
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Box>
          </Menu>

          {timeSegments.length > 0 && (
            <Tooltip title={`${showSegments ? 'Hide' : 'Show'} Chapters`} arrow>
              <IconButton
                onClick={handleToggleSegments}
                sx={{
                  ml: 2,
                  color: isAudioOnly ? theme.palette.text.primary : 'white',
                  '& svg': {
                    fill: 'none',
                    stroke: isAudioOnly ? theme.palette.text.primary : 'white',
                    strokeWidth: 1.75,
                    transition: theme.transitions.create(['transform'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    transform: 'scale(1)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    '& svg': {
                      transform: 'scale(1.3)',
                      stroke: isAudioOnly
                        ? theme.palette.text.primary
                        : 'white',
                    },
                  },
                }}
              >
                <AlignJustify />
              </IconButton>
            </Tooltip>
          )}

          {!isAudioOnly && (
            <>
              <Tooltip title="Picture in Picture" arrow>
                <IconButton
                  onClick={() => {
                    if (document.pictureInPictureElement) {
                      document.exitPictureInPicture();
                    } else if (videoRef.current) {
                      videoRef.current.requestPictureInPicture();
                    }
                  }}
                  sx={{
                    ...iconButtonSx,
                    ml: 2,
                    '& svg': {
                      fill: 'none',
                      stroke: isAudioOnly
                        ? theme.palette.text.primary
                        : 'white',
                      strokeWidth: 1.75,
                      transition: theme.transitions.create(['transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                    },
                    '&:hover svg': {
                      transform: 'scale(1.3)',
                    },
                  }}
                >
                  <PictureInPicture2 />
                </IconButton>
              </Tooltip>

              <Tooltip title="Full Screen" arrow>
                <IconButton
                  onClick={handleFullScreen}
                  sx={{
                    ...iconButtonSx,
                    ml: 2,
                    '& svg': {
                      fill: 'none',
                      stroke: isAudioOnly
                        ? theme.palette.text.primary
                        : 'white',
                      strokeWidth: 1.75,
                      transition: theme.transitions.create(['transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                    },
                    '&:hover svg': {
                      transform: 'scale(1.3)',
                    },
                  }}
                >
                  {document.fullscreenElement ? <Minimize /> : <Maximize />}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mt: 1 }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            height: '32px',
            width: '100%',
            py: 1,
          }}
          onMouseMove={handleSliderHover}
          onMouseLeave={handleSliderLeave}
        >
          <Slider
            value={displayProgress}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            aria-labelledby="progress-slider"
            sx={{
              width: '100%',
              color: theme.palette.primary.main,
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                zIndex: 3,
              },
              '& .MuiSlider-track': {
                height: 8,
                zIndex: 1,
                borderRadius: '4px',
              },
              '& .MuiSlider-rail': {
                height: 8,
                zIndex: 1,
                borderRadius: '4px',
                opacity: 0.8,
                backgroundColor: theme.palette.secondary.light,
              },
            }}
          />
          {showSegments &&
            timeSegments.map((segment, index) => {
              const startPosition = Math.min(
                Math.max(0, (segment.start / (externalDuration || 1)) * 100),
                100
              );
              const endPosition = Math.min(
                Math.max(0, (segment.end / (externalDuration || 1)) * 100),
                100
              );
              const width = endPosition - startPosition;

              const isFirst = index === 0;
              const isLast = index === timeSegments.length - 1;

              // Calculate completed and remaining portions
              const completedWidth = Math.max(
                0,
                Math.min(displayProgress - startPosition, width)
              );
              const remainingWidth = Math.max(0, width - completedWidth);

              return (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    left: !isFirst
                      ? `calc(${startPosition}% - 2px)`
                      : `${startPosition}%`,
                    bottom: '12px',
                    width: !isFirst ? `calc(${width}%)` : `${width}%`,
                    height: '8px',
                    zIndex: 2,
                    opacity: showSegments ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {/* Completed portion */}
                  {completedWidth > 0 && (
                    <Box
                      onClick={(e) => handleSegmentClick(e, segment.start)}
                      onMouseEnter={() => setHoveredSegment(index)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        width: `${(completedWidth / width) * 100}%`,
                        height: '100%',
                        backgroundColor: getSegmentBackgroundColor(
                          hoveredSegment === index,
                          true
                        ),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: 0,
                        borderTopLeftRadius: isFirst ? '4px' : 0,
                        borderBottomLeftRadius: isFirst ? '4px' : 0,
                        transform:
                          hoveredSegment === index
                            ? 'scaleY(1.5)'
                            : 'scaleY(1)',
                        transformOrigin: 'center',
                        zIndex: 3,
                      }}
                    />
                  )}

                  {/* Remaining portion */}
                  {remainingWidth > 0 && (
                    <Box
                      onClick={(e) => handleSegmentClick(e, segment.start)}
                      onMouseEnter={() => setHoveredSegment(index)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      sx={{
                        position: 'absolute',
                        left: `${(completedWidth / width) * 100}%`,
                        width: `${(remainingWidth / width) * 100}%`,
                        height: '100%',
                        backgroundColor: getSegmentBackgroundColor(
                          hoveredSegment === index,
                          false
                        ),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: 0,
                        borderTopRightRadius: isLast ? '4px' : 0,
                        borderBottomRightRadius: isLast ? '4px' : 0,
                        transform:
                          hoveredSegment === index
                            ? 'scaleY(1.5)'
                            : 'scaleY(1)',
                        transformOrigin: 'center',
                        zIndex: 3,
                        '&::before': !isFirst
                          ? {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              height: '100%',
                              width: '2px',
                              backgroundColor:
                                hoveredSegment === index
                                  ? theme.palette.primary.main
                                  : '#333333',
                              opacity: hoveredSegment === index ? 1 : 0.7,
                              transition: 'all 0.2s ease',
                            }
                          : undefined,
                      }}
                    />
                  )}

                  {/* Tooltip wrapper */}
                  <Box
                    onMouseEnter={() => setHoveredSegment(index)}
                    onMouseLeave={() => setHoveredSegment(null)}
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      zIndex: 2,
                      cursor: 'pointer',
                    }}
                  >
                    <Tooltip
                      title={segment.description}
                      placement="top"
                      arrow
                      open={hoveredSegment === index}
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white',
                            fontSize: '0.875rem',
                            maxWidth: 300,
                            p: 1,
                          },
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', height: '100%' }} />
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          {tooltipPosition !== null && (
            <Box
              sx={{
                position: 'absolute',
                top: '-20px',
                left: `${tooltipPosition}px`,
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                color: '#000',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.75rem',
                zIndex: 4,
              }}
            >
              {hoverTime}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  // Modify useEffect to handle both video and audio
  React.useEffect(() => {
    const mediaElement = videoRef.current;
    if (!mediaElement || !file) return undefined;

    // Force element to be visible and load
    mediaElement.style.display = 'block';
    if (mediaElement.readyState === 0) mediaElement.load();

    // Handle initial state based on media type
    if (!isAudioOnly && mediaElement instanceof HTMLVideoElement) {
      mediaElement.currentTime = 0.1;
    } else if (isAudioOnly && mediaElement instanceof HTMLAudioElement) {
      mediaElement.preload = 'auto';
    }

    const handlers = {
      waiting: () => setIsMediaLoading(true),
      playing: () => setIsMediaLoading(false),
      seeking: () => setIsMediaLoading(true),
      seeked: () => setIsMediaLoading(false),
    };

    // Add event listeners
    const entries = Object.entries(handlers);
    entries.forEach(([event, handler]) => {
      mediaElement.addEventListener(event, handler);
    });

    // Return cleanup function
    return () => {
      entries.forEach(([event, handler]) => {
        mediaElement.removeEventListener(event, handler);
      });
    };
  }, [file, videoRef.current, isAudioOnly]);

  return controlsLayout;
};

export default VideoControls;
